"use client";

import React from "react";
import { Clock, MapPin, Tag, CheckCircle2, Loader2, ChevronRight } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
const courseModules = [{
  title: "Módulo 1: Cómputos Métricos y Documentación",
  content: "Técnicas avanzadas de medición en planos y en obra. Organización de planillas Excel profesionales para el control de materiales, excavaciones, estructuras y terminaciones finas."
}, {
  title: "Módulo 2: Análisis de Costos Unitarios (ACU)",
  content: "Desglose exhaustivo de materiales, mano de obra y equipos. Cálculo de rendimientos reales y porcentajes de desperdicio. Creación de una base de datos de costos actualizada para el mercado local."
}, {
  title: "Módulo 3: Armado del Presupuesto y Gastos Generales",
  content: "Determinación de costos indirectos, gastos de oficina central y logística de obra. Aplicación del Coeficiente de Pase, beneficio industrial e impuestos para la obtención del precio de venta final."
}, {
  title: "Módulo 4: Control de Gestión y Redeterminación de Precios",
  content: "Seguimiento técnico-económico mediante certificaciones de avance. Aplicación de fórmulas polinómicas y métodos de ajuste para mitigar el impacto de la inflación y variaciones de mercado."
}];
function CourseForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-6 bg-primary/5 border border-primary/20 rounded-xl text-center space-y-4 animate-in fade-in zoom-in duration-300">
        <CheckCircle2 className="w-10 h-10 text-primary mx-auto" />
        <p className="text-sm font-medium text-foreground">{successMessage}</p>
        <Button className="w-full mt-2" onClick={handleSubmit}>
          inscribirse ahora
        </Button>
      </div>;
  }
  const inputBaseClass = "flex w-full rounded-md border border-input bg-muted/30 px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-colors";
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <FormField control={control} name="nombre" rules={{
      required: "El nombre es requerido"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Nombre Completo</FormLabel>
            <FormControl>
              <input type="text" className={`h-10 ${inputBaseClass}`} placeholder="Juan Pérez" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "El email es requerido",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Email inválido"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Correo Electrónico</FormLabel>
            <FormControl>
              <input type="email" className={`h-10 ${inputBaseClass}`} placeholder="juan@example.com" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FormField control={control} name="telefono" render={({
        field
      }) => <FormItem>
              <FormLabel>Teléfono (Opcional)</FormLabel>
              <FormControl>
                <input type="tel" className={`h-10 ${inputBaseClass}`} placeholder="+598 99 123 456" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="modalidad" render={({
        field
      }) => <FormItem>
              <FormLabel>Modalidad</FormLabel>
              <FormControl>
                <select className={`h-10 ${inputBaseClass}`} {...field}>
                  <option value="" disabled>Seleccione...</option>
                  <option value="Presencial">Presencial</option>
                  <option value="Online">Online</option>
                  <option value="Mixta">Mixta</option>
                </select>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="mensaje" render={({
      field
    }) => <FormItem>
            <FormLabel>Consulta o Comentario</FormLabel>
            <FormControl>
              <textarea className={`min-h-[80px] resize-y ${inputBaseClass}`} placeholder="Escriba aquí cualquier duda..." {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md">
          <p className="text-sm text-destructive">{submitError}</p>
        </div>}

      <Button type="submit" className="w-full" disabled={isSubmitting}>
        {isSubmitting ? <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Enviando...
          </> : "inscribirse ahora"}
      </Button>
    </Form>;
}
export default function DetallesDelCurso({
  isCorporate = false
}: {
  isCorporate?: boolean;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="detalles-del-curso" className="bg-background py-16 md:py-24 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl -z-10 pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-[30rem] h-[30rem] bg-secondary/5 rounded-full blur-3xl -z-10 pointer-events-none" />

      <div ref={ref} className={`container mx-auto px-4 max-w-7xl transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          
          {}
          <div className="lg:col-span-7 bg-background/70 backdrop-blur-[12px] rounded-[24px] border border-border p-6 md:p-8 shadow-sm">
            <div className="mb-8">
              <h2 className="text-3xl font-bold tracking-tight text-foreground mb-4">Programa del Curso</h2>
              <p className="text-muted-foreground text-lg">
                Un recorrido completo por las herramientas y metodologías necesarias para presupuestar obras con precisión y profesionalismo.
              </p>
            </div>

            <Accordion type="single" collapsible className="w-full" defaultValue="item-0">
              {courseModules.map((module, i) => <AccordionItem key={i} value={`item-${i}`} data-index={i} className="border-border/50">
                  <AccordionTrigger className="text-base md:text-lg font-semibold hover:text-primary transition-colors">
                    {module.title}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed text-base">
                    {module.content}
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>

          {}
          <div className="lg:col-span-5 lg:sticky lg:top-24">
            <Card className="rounded-[24px] border-border shadow-lg transition-all duration-300 hover:scale-[1.02] hover:shadow-xl hover:shadow-primary/5 overflow-hidden bg-card/95 backdrop-blur-sm">
              <CardHeader className="bg-muted/30 border-b border-border/50 pb-6">
                <CardTitle className="text-xl font-bold flex items-center gap-2">
                  <Tag className="w-5 h-5 text-primary" />
                  Ficha Técnica
                </CardTitle>
              </CardHeader>
              
              <CardContent className="pt-6 space-y-8">
                {}
                <div className="grid grid-cols-1 gap-4 font-mono text-sm">
                  <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30 border border-border/50">
                    <div className="flex items-center gap-3 text-muted-foreground">
                      <Clock className="w-4 h-4" />
                      <span>Duración</span>
                    </div>
                    <span className="font-semibold text-foreground">8 Semanas</span>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30 border border-border/50">
                    <div className="flex items-center gap-3 text-muted-foreground">
                      <MapPin className="w-4 h-4" />
                      <span>Modalidad</span>
                    </div>
                    <span className="font-semibold text-foreground">
                      {isCorporate ? "Online" : "Presencial"}
                    </span>
                  </div>

                  <div className="flex items-center justify-between p-4 rounded-lg bg-secondary/10 border border-secondary/20 mt-2">
                    <span className="font-semibold text-foreground">Inversión</span>
                    <span className="text-xl font-bold text-secondary">$U 12.000</span>
                  </div>
                </div>

                <div className="h-px w-full bg-border/50" />

                {}
                <div>
                  <h3 className="text-lg font-semibold mb-4">Formulario de Detalles del Curso</h3>
                  <FormProvider formId="course_details_form_presupuestacindeobras" sectionName="detalles-del-curso" defaultValues={{
                  nombre: "",
                  email: "",
                  telefono: "",
                  modalidad: "",
                  mensaje: ""
                }} submitText="Solicitar Información" successMessage="¡Gracias por tu interés! Te enviaremos la información detallada a la brevedad.">
                    <CourseForm />
                  </FormProvider>
                </div>
              </CardContent>
            </Card>
          </div>

        </div>
      </div>
    </section>;
}